import { inject } from '@angular/core';
import { CanActivateChildFn, Router, type ActivatedRouteSnapshot } from '@angular/router';

import { ApplicationFormStore, APPLICATION_SECTIONS } from './services/application-form.store';
import { ToastService } from '../../core/toast/toast.service';

/** Resolve a child route path (e.g. 'bar-admission') to its section key. */
const sectionKeyForRoute = (routePath: string | undefined): string | null => {
	if (!routePath) return null;
	return APPLICATION_SECTIONS.find((s) => s.route === routePath)?.key ?? null;
};

/**
 * Gate access to application-form sections.
 *
 * Hydrates the store from the server FIRST (idempotent — a no-op after the
 * first successful load), so a page reload or a pasted deep link resumes
 * correctly instead of bouncing to the start. We do the load inside the guard
 * rather than a resolver on purpose: Angular runs resolvers AFTER guards, so a
 * resolver's data wouldn't be available in time for this accessibility check.
 *
 * If the requested section isn't accessible yet (the applicant hasn't reached
 * it, or typed the URL directly), redirect to their resume point and explain
 * with a non-blocking toast.
 */
export const sectionAccessGuard: CanActivateChildFn = async (childRoute: ActivatedRouteSnapshot) => {
	const store = inject(ApplicationFormStore);
	const router = inject(Router);
	const toast = inject(ToastService);

	try {
		await store.loadFromServer();
	} catch {
		// A load failure shouldn't lock the applicant out — let them proceed with a
		// fresh in-memory form (the first section is always accessible).
	}

	const key = sectionKeyForRoute(childRoute.routeConfig?.path);
	// Unknown/paramless child (e.g. the '' redirect) — let the router handle it.
	if (!key) return true;

	if (store.isSectionAccessible(key)) return true;

	toast.info('Please complete the earlier sections first — we’ve taken you to where you left off.');
	return router.createUrlTree(['/application', store.resumeSection()]);
};
